import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Dialog from '@material-ui/core/Dialog'
import IconButton from '@material-ui/core/IconButton'
import SliderNew from '@components/slider-new'
import Hidden from '@material-ui/core/Hidden'
import { IconButtonCtaArrow, IconClose } from '@components/atoms/icons'
import { gtmEvent } from '@lib/gtm-utils'
import { useTrackCoachProfileEvent } from '@lib/pga-coach/coach-profile-analytics'
import { composeOptimizedImageUrl } from '@utils/composeOptimizedImageUrl'
import { useCoachProfileInColumn } from '@lib/pga-coach/coach-profile-layout'
import useStyles from './coach-photos.styles'

const GRID_IMAGE_WIDTH = 640
const LIGHTBOX_IMAGE_WIDTH = 1280

function CoachPhotos(props) {
  const {
    firstName,
    photos,
  } = props

  const [lightboxIndex, setLightboxIndex] = useState(null)
  const trackCoachProfileEvent = useTrackCoachProfileEvent()

  const transformHeicToJpg = (reference) => {
    return reference.replace('.heic', '.jpg')
  }

  const inColumn = useCoachProfileInColumn()
  const styles = useStyles({ inColumn })

  const photoSrc = (reference) => composeOptimizedImageUrl(transformHeicToJpg(reference), GRID_IMAGE_WIDTH)
  const photoSrcLightbox = (reference) => composeOptimizedImageUrl(transformHeicToJpg(reference), LIGHTBOX_IMAGE_WIDTH)

  const photoAlt = (index) => `${firstName} coaching photo ${index + 1}`

  const photoCount = photos?.length ?? 0
  const lightboxOpen = lightboxIndex !== null && lightboxIndex >= 0 && lightboxIndex < photoCount

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null)
  }, [])

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index)
    gtmEvent({
      event: 'click-coach-photos',
      attributes: { number: index + 1 },
    })
    trackCoachProfileEvent('click-coach-profile-photo', { number: index + 1 })
  }, [trackCoachProfileEvent])

  const goNext = useCallback(() => {
    setLightboxIndex((i) => {
      if (i === null || photoCount < 2) return i
      return (i + 1) % photoCount
    })
  }, [photoCount])

  const goPrev = useCallback(() => {
    setLightboxIndex((i) => {
      if (i === null || photoCount < 2) return i
      return (i - 1 + photoCount) % photoCount
    })
  }, [photoCount])

  useEffect(() => {
    if (!lightboxOpen) return undefined
    const onKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        goNext()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goPrev()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [lightboxOpen, goNext, goPrev])

  const slides = photos.map((photo, index) => (
    <div key={`photo-${photo.rank}`}>
      <div className={styles.slideWrapper}>
        <img
          className={styles.slideImage}
          src={photoSrc(photo.reference)}
          alt={photoAlt(index)}
        />
      </div>
    </div>
  ))

  const config = {
    initial: 0,
    loop: true,
    mode: 'snap',
    slides: { origin: 'center', perView: 1, spacing: 8 },
    breakpoints: {
      '(min-width: 600px)': {
        loop: false,
        slides: { perView: 'auto', spacing: 28 },
      },
    },
  }

  return photos && photos.length && (
    <>
      <Hidden smUp>
        <Box id="coach-profile-photos" className={styles.mobileSection}>
          <Typography
            variant="h5"
            component="h2"
            className={`${styles.sectionHeading} ${styles.sectionHeadingMobile}`}
          >
            Photos
          </Typography>
          <Box className={styles.mobileSliderShell}>
            <SliderNew
              className={styles.mobileKeenSlider}
              controlClassName={styles.customControl}
              config={config}
            >
              {slides}
            </SliderNew>
          </Box>
        </Box>
      </Hidden>
      <Hidden xsDown>
        <Box id="coach-profile-photos" className={styles.testimonialsWrapper}>
          <Box className={styles.photosSectionContainer}>
            <Box className={styles.photosDesktopInner}>
              <Typography variant="h5" component="h2" className={styles.sectionHeading}>
                Photos
              </Typography>
              <Box className={styles.photosGrid}>
                {photos.map((photo, index) => (
                  <Box
                    key={`photo-grid-${photo.rank}-${photo.reference}`}
                    className={styles.photoGridCell}
                  >
                    <button
                      type="button"
                      className={styles.photoGridTrigger}
                      onClick={() => openLightbox(index)}
                    >
                      <img
                        className={styles.gridPhoto}
                        src={photoSrc(photo.reference)}
                        alt={photoAlt(index)}
                      />
                    </button>
                  </Box>
                ))}
              </Box>
              <Dialog
                open={lightboxOpen}
                onClose={closeLightbox}
                maxWidth={false}
                fullWidth={false}
                PaperProps={{ elevation: 0, className: styles.lightboxPaper }}
                aria-label={
                  lightboxOpen
                    ? `${firstName} in action, photo ${lightboxIndex + 1} of ${photoCount}`
                    : undefined
                }
              >
                <Box className={styles.lightboxRoot}>
                  <IconButton
                    className={styles.lightboxClose}
                    onClick={closeLightbox}
                    aria-label="Close photo viewer"
                  >
                    <IconClose />
                  </IconButton>
                  {photoCount > 1 && (
                    <IconButton
                      className={`${styles.lightboxNav} ${styles.lightboxNavPrev}`}
                      onClick={goPrev}
                      aria-label="Previous photo"
                    >
                      <IconButtonCtaArrow className={styles.lightboxNavArrow} width={21} height={12} />
                    </IconButton>
                  )}
                  {lightboxOpen && (
                    <img
                      className={styles.lightboxImage}
                      src={photoSrcLightbox(photos[lightboxIndex].reference)}
                      alt={photoAlt(lightboxIndex)}
                    />
                  )}
                  {photoCount > 1 && (
                    <IconButton
                      className={`${styles.lightboxNav} ${styles.lightboxNavNext}`}
                      onClick={goNext}
                      aria-label="Next photo"
                    >
                      <IconButtonCtaArrow width={21} height={12} />
                    </IconButton>
                  )}
                </Box>
              </Dialog>
            </Box>
          </Box>
        </Box>
      </Hidden>
    </>
  )
}

CoachPhotos.propTypes = {
  firstName: PropTypes.string,
  photos: PropTypes.array,
}

export default CoachPhotos
